import React from "react";
import { Phone, ArrowRight, ShieldCheck, Clock, CircleCheck as CheckCircle2 } from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
export default function Hero() {
  const specs = [{
    label: "Response Time",
    value: "60-Minute Local Dispatch"
  }, {
    label: "Hazard Rating",
    value: "IICRC Category 3 Certified"
  }, {
    label: "Direct Billing",
    value: "All Insurance Carriers"
  }];
  return <section id="hero" data-nav="dark" className="relative min-h-[680px] lg:min-h-[760px] flex flex-col justify-between overflow-hidden bg-foreground text-background">
      {/* Background field photo with dark industrial scrim */}
      <div className="absolute inset-0 z-0 select-none pointer-events-none">
        <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1801371/img/sewage_cleanup_technician_basement.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1801371/img/sewage_cleanup_technician_basement.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1801371/img/sewage_cleanup_technician_basement.png-wvc-sizes" alt="TriServe technician in biological PPE performing emergency sewage cleanup in residential basement" className="h-full w-full object-cover object-center" loading="eager" />
        {/* Multi-stop industrial scrim ensuring strict contrast and legibility */}
        <div className="absolute inset-0 bg-gradient-to-b from-foreground/85 via-foreground/80 to-foreground/95" />
      </div>

      {/* Top operational alert banner */}
      <div className="relative z-10 border-b border-border/20 bg-foreground/40 backdrop-blur-xs">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-2.5">
          <div className="flex flex-wrap items-center justify-between gap-3 text-xs md:text-sm font-mono text-background/90">
            <div className="flex items-center gap-2">
              <span className="relative flex h-2.5 w-2.5">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-accent opacity-75" />
                <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-accent" />
              </span>
              <span className="font-semibold tracking-wide uppercase">
                Live 24/7 Emergency Dispatch Active
              </span>
            </div>
            <div className="hidden sm:flex items-center gap-6 text-background/80">
              <span className="flex items-center gap-1.5">
                <ShieldCheck className="size-4 text-accent" />
                Category 3 Blackwater Containment
              </span>
              <span className="flex items-center gap-1.5">
                <Clock className="size-4 text-accent" />
                Direct Local Technicians (No Call Centers)
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Main hero content */}
      <div className="relative z-10 mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-16 md:py-24 lg:py-28 flex-1 flex flex-col justify-center">
        <div className="max-w-3xl">
          {/* Operational badge */}
          <div className="inline-flex items-center gap-2 rounded-full border border-accent/40 bg-accent/15 px-3 py-1 text-xs font-semibold uppercase tracking-wider text-accent mb-6">
            <span className="h-1.5 w-1.5 rounded-full bg-accent" />
            Maryland • Washington DC • Northern Virginia
          </div>

          {/* Heading */}
          <h1 className="font-default text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-background leading-[1.08] mb-6">
            Sewage cleanup
          </h1>

          {/* Supporting line */}
          <p className="text-lg sm:text-xl md:text-2xl font-medium text-background/95 leading-snug mb-5">
            Rapid biohazard containment, immediate blackwater extraction, and complete hospital-grade decontamination.
          </p>

          {/* Context body copy */}
          <p className="text-base sm:text-lg text-background/80 leading-relaxed max-w-2xl mb-8">
            Sewage backups contain dangerous bacteria, viruses, and parasites that require professional mitigation. Our certified technicians arrive on-site with commercial antimicrobial gear, industrial extractors, and negative air controls to restore a clean, safe environment immediately.
          </p>

          {/* Action triggers */}
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-4 mb-10">
            <Button asChild size="lg" className="bg-primary text-primary-foreground hover:bg-primary/90 font-bold px-7 h-12 shadow-md transition-colors">
              <a href="tel:855-435-6631" className="flex items-center justify-center gap-2.5">
                <Phone className="size-5 text-primary-foreground" />
                <span>Call Now</span>
                <span className="font-mono text-sm opacity-90">(855-435-6631)</span>
              </a>
            </Button>

            <Button asChild size="lg" className="bg-accent text-accent-foreground hover:bg-accent/90 border border-accent font-extrabold px-7 h-12 shadow-md transition-colors">
              <Link to="/ContactRequestServicePage" className="flex items-center justify-center gap-2">
                <span>Request Service</span>
                <ArrowRight className="size-4 text-accent-foreground" />
              </Link>
            </Button>
          </div>

          {/* Quick reassurance points */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 pt-2 text-sm text-background/85">
            <div className="flex items-center gap-2">
              <CheckCircle2 className="size-4 text-accent shrink-0" />
              <span>Direct insurance billing</span>
            </div>
            <div className="flex items-center gap-2">
              <CheckCircle2 className="size-4 text-accent shrink-0" />
              <span>On-scene in under 60 min</span>
            </div>
            <div className="flex items-center gap-2">
              <CheckCircle2 className="size-4 text-accent shrink-0" />
              <span>EPA-registered disinfectants</span>
            </div>
          </div>
        </div>
      </div>

      {/* Technical Spec Bar */}
      <div className="relative z-10 border-t border-border/20 bg-foreground/60 backdrop-blur-sm">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-4">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 divide-y sm:divide-y-0 sm:divide-x divide-border/20">
            {specs.map((spec, i) => <div key={spec.label} data-index={i} className={`flex flex-col ${i > 0 ? "pt-3 sm:pt-0 sm:pl-6" : ""}`}>
                <span className="font-mono text-[11px] uppercase tracking-wider text-background/60">
                  {spec.label}
                </span>
                <span className="font-semibold text-sm sm:text-base text-background">
                  {spec.value}
                </span>
              </div>)}
          </div>
        </div>
      </div>
    </section>;
}